import { useDedupEffect } from "shared/hooks/useDedupEffect";
import { fetchResources, response } from "shared/services/fetchResources";
import { environment, getOnlineServices } from "shared/util/index";
import { useDispatch, useSelector } from "react-redux";
import { createLaunchFailureAction, createSetConfigAction, createSetContentAction, createSetResourcesLoaded,
createSetUpdateManfiestAction, createSetViewAction } from "shared/state/reducer";
import { indexDBStorage } from "shared/storage/indexDBStorage";
import { IADPState } from "shared/typings";
import { useIPCBridge } from "shared/hooks/useIPCBridge";
import { useBatteryService } from "shared/hooks/useBatteryService";
import { useNetworkStatus } from "shared/hooks/useNetworkStatus";
import { useImagePreloader } from "shared/hooks/useImagePreloader";
import { useServerStatus } from "shared/hooks/useServerStatus";
import { useDateTimeValidation } from "shared/hooks/useDateTimeValidation";
import { LoggPrefix } from "shared/util/constants";
import { useServiceWorkerRegistration } from "./useServiceWorkerRegistration";
import { useDeviceMetricsService } from "./useDeviceMetricsService";
export function useInitilizeApp() {
    const dispatch = useDispatch();
    useNetworkStatus();
    useServerStatus();
    useImagePreloader();
    useDateTimeValidation();
    const {patchDeviceMetrics} = useDeviceMetricsService();
    const serviceWorkerRegistration  = useServiceWorkerRegistration();
    const state = useSelector((state: { rootState: IADPState }) => state.rootState);
    useBatteryService();
    const { sendGreeting, getReleaseVersion } = useIPCBridge();
    async function fetchAppData() {
        console.log(LoggPrefix,"[InitilizeApp]", "load app resources");
        try {     
            let promises = [];
            const fetchConfig = new Promise<response>(async (resolve) => {
                let configData = await fetchResources(`${environment.resovleAppDomain()}config/config.json`);
                try {
                    if(configData.error){
                        console.log(LoggPrefix,"fallback to indexDB for config");
                        configData =  await (await indexDBStorage.getInstance()).transactions.get("adp-session-references", "config"); 
                    }
                    else {
                        await (await indexDBStorage.getInstance()).transactions.put("adp-session-references", configData, "config"); 
                    }
                    dispatch(createSetConfigAction(configData.data));
                    resolve(configData);  
                }
                catch(e){
                    console.warn(LoggPrefix, "failed to fetch app config",e); 
                    configData =  await (await indexDBStorage.getInstance()).transactions.get("adp-session-references", "config"); 
                    if(configData){
                        resolve(configData);  
                    }
                    else {
                        resolve({error: true}); 
                    } 
                }
            })
            const fetchManifest = new Promise<response>(async (resolve) => {
                let manifestData = undefined;
                 try {
                    const locManifestData =  await (await indexDBStorage.getInstance()).transactions.get("adp-session-references", "manifestData");
                    const appReleaseVersion = await getReleaseVersion();
                    if(!locManifestData){
                        //fetch remote
                        const remoteManifestData = await fetchResources(`${environment.resovleAppDomain()}manifest/naep.manifest.json`);
                        manifestData = remoteManifestData;
                      if(!remoteManifestData.error){
                        await (await indexDBStorage.getInstance()).transactions.put("adp-session-references", remoteManifestData, "manifestData")
                        } 
                     }
                     else {
                        manifestData = locManifestData;
                     }
                     
                    const version = appReleaseVersion ?? manifestData?.data?.manifest.product?.version
                    if(manifestData?.data?.manifest.product?.version){
                        //override version from secure browser
                        manifestData.data.manifest.product.version = version 
                    }
                    await (await indexDBStorage.getInstance()).transactions.put("adp-session-references", version, "localReleaseVersion");
                    dispatch(createSetUpdateManfiestAction(manifestData.data))
                }
                catch (e) {
                    console.warn("error updating release version in indexDB");
                    resolve({error: true}); 
                }
                resolve(manifestData);
            })
            const fetchLocales = new Promise<response>(async (resolve) => {
                const enData = await fetchResources(`${environment.resovleAppDomain()}locale/en.json`);
                indexDBStorage.getInstance().then((instance: any) => {
                    instance.transactions.put("adp-session-references", enData.data, "content-en");
                });
                const esData = await fetchResources(`${environment.resovleAppDomain()}locale/es.json`);
                indexDBStorage.getInstance().then((instance: any) => {
                    instance.transactions.put("adp-session-references", esData.data, "content-es");
                });
                dispatch(createSetContentAction({ en: enData.data, es: esData.data }));
                resolve(enData);
            })
            promises.push(fetchConfig);
            promises.push(fetchManifest);
            promises.push(fetchLocales);
              await Promise.all(promises).then(async (values) => {
                const responseError = values.find((val) => { return val.error });
                if (responseError) {
                    dispatch(createLaunchFailureAction({error:"CachingError",errorDetails:{timeStamp: new Date(),errorMessage:`There was an error getting the config, manifest, or locale file. Config Error: ${values[0].error}, Manifest Error: ${values[1].error}, Locale Error: ${values[2].error}`}}))
                    dispatch(createSetViewAction("WarningView"));
                }
                else {
                    dispatch(createSetResourcesLoaded(true));
                    await (await indexDBStorage.getInstance()).transactions.put("adp-session-references", true, "offline-ready");
                }
            })
        }
        catch (e) {
            console.log("Error initiliziing app", e);
            dispatch(createLaunchFailureAction({error:"CachingError",errorDetails:{timeStamp: new Date(),errorMessage:`There was an error initilizing the app. Error: ${e}`}}))
            dispatch(createSetViewAction("WarningView"));
        }
    }
    useDedupEffect(() => {
        if (state.appState.resourcesLoaded) {
            console.log("[InitilizeApp]", "app initialized, send greeting to extension");
            sendGreeting();
        }
    }, [state.appState.resourcesLoaded]);
    useDedupEffect(()=>{
        if(!serviceWorkerRegistration.serviceWorkerRegistration){
            fetchAppData();
        }
    },[serviceWorkerRegistration.serviceWorkerRegistration])  
    useDedupEffect(() => {
        if(state.sessionState.schoolDetails?.setupCode){
          const deviceMetricsURl = `${environment.resolveApiDomain(state.appState.configuration)}/${getOnlineServices(state.appState.configuration)?.postDeviceMetrics}`
          if(!deviceMetricsURl) return
          patchDeviceMetrics(deviceMetricsURl);
        }
      }, [state.deviceState.batteryState.charging,state.deviceState.batteryState.level]);
};

